import { formatNameWithSuffix, formatSuffix } from "./name.jsx";

/**
 * Aligns with family tree root logic: 1 = male, 2 = female (string aliases like API may send).
 */
function normalizeGender(value) {
  if (value === null || value === undefined || value === "") return null;
  if (value === 1 || value === "1") return 1;
  if (value === 2 || value === "2") return 2;
  if (value === 3 || value === "3") return null;
  if (typeof value === "string") {
    const g = value.toLowerCase().trim();
    if (g === "male" || g === "m") return 1;
    if (g === "female" || g === "f") return 2;
  }
  return null;
}

/**
 * Last-name parts for the tree root banner (LabelNode) — same rules for `treeData[0]`
 * and `loggedIn_user_role.top_of_the_line`.
 * @returns {{ mainMemberName: string, spouseName: string, mainMemberGender: number|null, spouseNameGender: number|null }}
 */
export function getFamilyTreeRootNameParts(rootLike) {
  const empty = {
    mainMemberName: "",
    spouseName: "",
    mainMemberGender: null,
    spouseNameGender: null,
  };
  if (!rootLike || typeof rootLike !== "object") return empty;

  let rootGender = normalizeGender(
    rootLike.gender ??
      rootLike.Gender ??
      rootLike.member_gender ??
      rootLike.memberGender ??
      rootLike.head_gender ??
      rootLike.headGender
  );
  let spouseGender = normalizeGender(
    rootLike.spouse_gender ??
      rootLike.spouseGender ??
      rootLike.spouse_gender_id ??
      rootLike.spouseGenderId
  );

  // If API sends only one gender (common on top_of_the_line), infer the other like a head+spouse pair.
  if (rootGender && !spouseGender) {
    spouseGender = rootGender === 1 ? 2 : 1;
  } else if (spouseGender && !rootGender) {
    rootGender = spouseGender === 1 ? 2 : 1;
  }

  const lastName = rootLike.last_name ?? rootLike.lastName ?? "";
  const maidenName = rootLike.maiden_name ?? rootLike.maidenName ?? "";
  const spouseLast = rootLike.spouse_last_name ?? rootLike.spouseLastName ?? "";
  const spouseMaiden =
    rootLike.spouse_maiden_name ?? rootLike.spouseMaidenName ?? "";

  let mainMemberName = "";
  let spouseName = "";
  let mainMemberGender = null;
  let spouseNameGender = null;

  const hasGenderHints = rootGender === 1 || rootGender === 2;

  if (hasGenderHints) {
    if (rootGender === 1) {
      mainMemberName = String(lastName).trim();
      mainMemberGender = 1;
    } else if (rootGender === 2) {
      mainMemberName = String(maidenName || lastName).trim();
      mainMemberGender = 2;
    }
    if (spouseGender === 2) {
      spouseName = String(spouseMaiden || spouseLast).trim();
      spouseNameGender = 2;
    } else if (spouseGender === 1) {
      spouseName = String(spouseLast).trim();
      spouseNameGender = 1;
    }
  } else {
    mainMemberName = String(lastName).trim();
    spouseName = String(spouseMaiden || spouseLast).trim();
  }

  // Married same surname and no separate maiden in payload → one banner surname (not "X & X").
  if (
    mainMemberName &&
    spouseName &&
    mainMemberName.toLowerCase() === spouseName.toLowerCase()
  ) {
    spouseName = "";
    spouseNameGender = null;
  }

  return {
    mainMemberName,
    spouseName,
    mainMemberGender,
    spouseNameGender,
  };
}

/** Single space between each character in a segment (e.g. "Pat" → "P a t"). */
function spaceLetters(segment) {
  if (!segment) return "";
  return Array.from(segment).join(" ");
}

/** Four spaces before and after the ampersand (header uses white-space: pre-wrap). */
const SPACES_AROUND_AMP = "    ";

/**
 * Header display: spaced letters per segment; four spaces before and after `&`; same four spaces before "Family".
 * Example: "Witherspoon & Patterson Family" → "W i t h e r s p o o n    &    P a t t e r s o n    F a m i l y"
 */
export function formatTopOfLineFamilyTitle(rootLike) {
  const { mainMemberName, spouseName } = getFamilyTreeRootNameParts(rootLike);
  if (!mainMemberName) return "";

  const mainSpaced = spaceLetters(mainMemberName.trim());
  const familySpaced = spaceLetters("Family");
  const gapBeforeFamily = SPACES_AROUND_AMP;

  if (spouseName && String(spouseName).trim()) {
    const spouseSpaced = spaceLetters(String(spouseName).trim());
    return `${mainSpaced}${SPACES_AROUND_AMP}&${SPACES_AROUND_AMP}${spouseSpaced}${gapBeforeFamily}${familySpaced}`;
  }

  return `${mainSpaced}${gapBeforeFamily}${familySpaced}`;
}

/** Returns the unspaced family title (e.g. "Witherspoon & Patterson Family") */
export function getUnspacedTopOfLineFamilyTitle(rootLike) {
  const { mainMemberName, spouseName } = getFamilyTreeRootNameParts(rootLike);
  if (!mainMemberName) return "";

  if (spouseName && String(spouseName).trim()) {
    return `${mainMemberName.trim()} & ${String(spouseName).trim()} Family`;
  }

  return `${mainMemberName.trim()} Family`;
}

/** Join non-empty name parts with single spaces. */
function trimJoinParts(...parts) {
  return parts
    .filter((p) => p != null && String(p).trim() !== "")
    .map((p) => String(p).trim())
    .join(" ");
}

/**
 * Drops tokens matching the main person's last name from the spouse display
 * (e.g. spouse listed as "Frances Virginia Witherspoon Patterson" → remove duplicate husband surname).
 */
function stripMainLastNameFromSpouseDisplay(spouseDisplay, mainLastName) {
  const ml = String(mainLastName || "").trim().toLowerCase();
  if (!ml || !spouseDisplay) return (spouseDisplay || "").trim();
  const tokens = String(spouseDisplay)
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const filtered = tokens.filter((t) => t.toLowerCase() !== ml);
  return filtered.join(" ").trim();
}

/**
 * Display line for Start/Top of the Line when `top_of_the_line` includes full names
 * (e.g. "Joseph Alexander Witherspoon & Frances Virginia Patterson").
 * Main: `first_name`, `middle_name`, `last_name` (no suffix).
 * Spouse: `spouse_first_name`, `spouse_middle_name`, `spouse_maiden_name`
 * (falls back to `spouse_last_name` if maiden is absent).
 * Combined-string fallbacks strip duplicate tokens matching main `last_name`.
 * Then {@link getUnspacedTopOfLineFamilyTitle}.
 */
export function getFullNamesTopOfLineDisplay(rootLike) {
  if (!rootLike || typeof rootLike !== "object") return "";

  const first = rootLike.first_name ?? rootLike.firstName ?? "";
  const middle = rootLike.middle_name ?? rootLike.middleName ?? "";
  const last = rootLike.last_name ?? rootLike.lastName ?? "";

  const sf = rootLike.spouse_first_name ?? rootLike.spouseFirstName ?? "";
  const sm = rootLike.spouse_middle_name ?? rootLike.spouseMiddleName ?? "";
  const spouseThird =
    rootLike.spouse_maiden_name ??
    rootLike.spouseMaidenName ??
    rootLike.spouse_last_name ??
    rootLike.spouseLastName ??
    "";

  const mainFull = trimJoinParts(first, middle, last);
  const spouseFull = trimJoinParts(sf, sm, spouseThird);

  if (mainFull && spouseFull) return `${mainFull} & ${spouseFull}`;
  if (mainFull) return mainFull;
  if (spouseFull) return spouseFull;

  const mainCombined = [rootLike.full_name, rootLike.fullName, rootLike.name].find(
    (v) => v != null && String(v).trim() !== ""
  );
  const spouseCombinedRaw = [rootLike.spouse_full_name, rootLike.spouseFullName].find(
    (v) => v != null && String(v).trim() !== ""
  );
  const spouseCombined = spouseCombinedRaw
    ? stripMainLastNameFromSpouseDisplay(String(spouseCombinedRaw).trim(), last)
    : "";

  if (mainCombined && spouseCombined) {
    return `${String(mainCombined).trim()} & ${spouseCombined}`;
  }
  if (mainCombined) {
    return String(mainCombined).trim();
  }

  return getUnspacedTopOfLineFamilyTitle(rootLike);
}

/** Suffix from a string or `{ name }` object, as the tree node reads it. */
function resolveSuffix(raw) {
  if (raw == null || raw === "") return "";
  if (typeof raw === "object") {
    return String(raw.name ?? raw.suffix ?? raw.value ?? "").trim();
  }
  return String(raw).trim();
}

/** Four-digit year from a ms timestamp, ISO string or `MM-DD-YYYY` style date. */
function toYear(value) {
  if (value == null || value === "") return "";
  const raw = String(value).trim();

  if (/^-?\d+$/.test(raw)) {
    const date = new Date(Number(raw));
    return Number.isNaN(date.getTime()) ? "" : String(date.getUTCFullYear());
  }

  const parsed = Date.parse(raw);
  if (!Number.isNaN(parsed)) {
    return String(new Date(parsed).getUTCFullYear());
  }

  const match = raw.match(/\b(\d{4})\b/);
  return match ? match[1] : "";
}

/** "1894-1953" like the tree node; empty unless both years resolve. */
function lifeYears(dob, dateOfDeath) {
  const birth = toYear(dob);
  const death = toYear(dateOfDeath);
  if (!birth || !death) return "";
  return `${birth}-${death}`;
}

/**
 * Start/Top of the Line rendered the way the tree's generation-1 node renders it:
 * the main member on the first line and the spouse on the second, each with its own
 * gender colour, suffix and life years. Spouse keeps the gen-1 maiden format
 * ("First Middle (Maiden) Last, Suffix").
 * Returns an empty array when the payload has no structured name parts, so callers
 * can fall back to {@link getFullNamesTopOfLineDisplay}.
 *
 * @returns {Array<{ key: string, name: string, gender: number|null, years: string }>}
 */
export function getTopOfLineDisplayLines(rootLike) {
  if (!rootLike || typeof rootLike !== "object") return [];

  const { mainMemberGender, spouseNameGender } = getFamilyTreeRootNameParts(rootLike);

  const first = rootLike.first_name ?? rootLike.firstName ?? "";
  const middle = rootLike.middle_name ?? rootLike.middleName ?? "";
  const last = rootLike.last_name ?? rootLike.lastName ?? "";
  const suffix = resolveSuffix(rootLike.suffix ?? rootLike.suffix_name);

  const spouseFirst = rootLike.spouse_first_name ?? rootLike.spouseFirstName ?? "";
  const spouseMiddle = rootLike.spouse_middle_name ?? rootLike.spouseMiddleName ?? "";
  const spouseLast = rootLike.spouse_last_name ?? rootLike.spouseLastName ?? "";
  const spouseMaiden = String(
    rootLike.spouse_maiden_name ?? rootLike.spouseMaidenName ?? ""
  ).trim();
  const spouseSuffix = resolveSuffix(
    rootLike.spouse_suffix ?? rootLike.spouseSuffix ?? rootLike.spouse_suffix_name
  );

  const mainName = formatNameWithSuffix(first, middle, last, suffix);

  // Same rule as FamilyMemberNode for generation 1: maiden name in parentheses
  // before the married surname.
  let spouseName = "";
  if (spouseMaiden && (spouseFirst || spouseLast)) {
    const beforeMaiden = [spouseFirst, spouseMiddle]
      .filter((p) => p != null && String(p).trim() !== "")
      .map((p) => String(p).trim())
      .join(" ");
    spouseName = beforeMaiden ? `${beforeMaiden} (${spouseMaiden})` : `(${spouseMaiden})`;
    if (spouseLast) spouseName = `${spouseName} ${String(spouseLast).trim()}`.trim();
    if (spouseSuffix) spouseName = `${spouseName}, ${formatSuffix(spouseSuffix)}`;
  } else {
    spouseName = formatNameWithSuffix(
      spouseFirst,
      spouseMiddle,
      spouseLast || spouseMaiden,
      spouseSuffix
    );
  }

  // No structured name parts — callers fall back to the plain combined string.
  if (!mainName && !spouseName) return [];

  const mainYears = lifeYears(
    rootLike.dob ?? rootLike.date_of_birth ?? rootLike.dateOfBirth,
    rootLike.date_of_death ?? rootLike.dateOfDeath ?? rootLike.dod
  );
  const spouseYears = lifeYears(
    rootLike.spouse_dob ?? rootLike.spouseDob ?? rootLike.spouse_date_of_birth,
    rootLike.spouse_date_of_death ?? rootLike.spouseDateOfDeath ?? rootLike.spouse_dod
  );

  const lines = [];
  if (mainName) {
    lines.push({
      key: "main",
      name: mainName,
      gender: mainMemberGender,
      years: mainYears,
    });
  }
  if (spouseName) {
    lines.push({
      key: "spouse",
      name: spouseName,
      gender: spouseNameGender,
      years: spouseYears,
    });
  }
  return lines;
}
